/**
 * Enhanced Authentication Context (TypeScript)
 * Provides authentication state and methods to the entire application
 * Updated to work with server-side OIDC authentication
 */

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

// User type for server-side auth
export interface User {
  id: string;
  email: string;
  name?: string;
  username?: string;
  role?: string;
  groups?: string[];
  permissions?: string[];
}

// Context value type
export interface AuthContextValue {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  checkAuthState: () => Promise<void>;
  clearError: () => void;
}

// Default context value
const defaultAuthContext: AuthContextValue = {
  user: null,
  loading: true,
  error: null,
  isAuthenticated: false,
  login: () => {},
  logout: () => {},
  checkAuthState: async () => {},
  clearError: () => {},
};

// Create the context
const AuthContext = createContext<AuthContextValue>(defaultAuthContext);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Props for AuthProvider
interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider Component
 * Manages authentication state and provides auth methods to the application
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  /**
   * Clear all local storage and session storage data
   */
  const clearAllStorage = useCallback(() => {
    try {
      localStorage.clear();
      sessionStorage.clear();
      console.log('All storage cleared');
    } catch (error) {
      console.warn('Error clearing storage:', error);
    }
  }, []);

  /**
   * SIMPLIFIED: Check authentication state only once
   */
  const checkAuthState = useCallback(async () => {
    // Prevent multiple checks
    if (hasCheckedAuth) {
      console.log('Auth already checked, skipping');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production' ||
                          process.env.NODE_ENV === 'production';
      const apiUrl = isProduction ? '' : (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '');

      // Simple auth check - no cache busting needed
      const response = await fetch(`${apiUrl}/api/auth/status`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.authenticated && data.user) {
          setUser(data.user);
          setError(null);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
      
      setHasCheckedAuth(true);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setHasCheckedAuth(true);
    } finally {
      setLoading(false);
    }
  }, [hasCheckedAuth]);

  /**
   * SIMPLIFIED: Redirect to backend login route
   */
  const login = useCallback(() => {
    const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production' ||
                        process.env.NODE_ENV === 'production';
    const apiUrl = isProduction ? '' : (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '');
    
    // Redirect to /login which will redirect to Cognito for authentication
    window.location.href = `${apiUrl}/login`;
  }, []);

  /**
   * Enhanced logout with proper Cognito logout flow
   */
  const logout = useCallback(async () => {
    try {
      // Clear local state and storage immediately
      setUser(null);
      clearAllStorage();
      setHasCheckedAuth(false);

      const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production' ||
                          process.env.NODE_ENV === 'production';
      const apiUrl = isProduction ? '' : (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '');

      // Call backend logout endpoint to get Cognito logout URL
      const response = await fetch(`${apiUrl}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Logout response:', data);
        
        if (data.logoutUrl) {
          // Clear any remaining cookies on the client side
          document.cookie.split(";").forEach((c) => {
            document.cookie = c
              .replace(/^ +/, "")
              .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
          });
          
          // Small delay to ensure all cleanup is complete
          setTimeout(() => {
            // Redirect to Cognito logout URL which will sign out from Cognito
            // and then redirect back to our application
            window.location.href = data.logoutUrl;
          }, 100);
        } else {
          // Fallback: redirect directly to logout endpoint
          window.location.href = `${apiUrl}/logout`;
        }
      } else {
        // On error, try GET request to logout endpoint as fallback
        console.warn('POST logout failed, trying GET fallback');
        window.location.href = `${apiUrl}/logout`;
      }
    } catch (error) {
      console.error('Logout error:', error);
      // On any error, redirect to logout endpoint as last resort
      const isProduction = process.env.REACT_APP_ENVIRONMENT === 'production' ||
                          process.env.NODE_ENV === 'production';
      const apiUrl = isProduction ? '' : (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE_URL || '');
      window.location.href = `${apiUrl}/logout`;
    }
  }, [clearAllStorage]);

  /**
   * Clear any authentication errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // SIMPLIFIED: Check auth state only once on mount
  useEffect(() => {
    checkAuthState();
  }, []); // Empty dependency array - only run once

  // Reset hasCheckedAuth when user changes
  useEffect(() => {
    if (user === null) {
      setHasCheckedAuth(false);
    }
  }, [user]);

  // Context value
  const value: AuthContextValue = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuthState,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Higher-order component for requiring authentication
 */
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WrappedComponent: React.FC<P> = (props) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" />
        </div>
      );
    }

    if (!isAuthenticated) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h1>
            <p className="text-gray-600">
              Please sign in to access this application.
            </p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };

  WrappedComponent.displayName = `withAuth(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default AuthProvider;